import React, { useState } from 'react';
import './CS.css';
import { NavLink } from 'react-router-dom';
import { MathJax, MathJaxContext } from "better-react-mathjax";
import PageNavigator from "../../../components/PageNavigator";
import ExampleBox from "../../../components/ExampleBox";


function CS2() {
  return (
        <div className='CS'>
        <PageNavigator group="CS" />
        <p className='heading1'>Weighted Average Cost of Capital</p>
        <MathJaxContext>
        <p className='heading2'>Valuation Discount Rate</p>
        <p>
        We want to find the value of a firm using a discounted cash flow analysis. 
        Suppose we have estimated the expected free cash flows that the firm's assets will generate over the next five years, along with a terminal value. 
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            CF_1,\\;CF_2,\\;CF_3,\\;CF_4,\\;CF_5,\\;TV
            \\end{aligned}
          \\]`}
        </MathJax>  
        We can discount these cash flows back to present value to find the total value of the firm:
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            V = \\frac{CF_1}{1 + r} + \\frac{CF_2}{(1 + r)^2} + \\frac{CF_3}{(1 + r)^3} + \\frac{CF_4}{(1 + r)^4} + \\frac{CF_5 + TV}{(1 + r)^5} 
            \\end{aligned}
          \\]`}
        </MathJax>   
        What discount rate, <MathJax inline>{"\\( r \\)"}</MathJax>, should we use to bring these cash flows back to present value? 
        </p>

        <p className='heading2'>Perpetuity Assumption</p>
        <p>
        To evaluate this discount rate from first principles, we analyze the firm as a perpetuity. 
        Instead of modeling an abrupt firm liquidation or complex multi-year steps, we assume the firm's assets generate a constant distribution of random cash flows <MathJax inline>{"\\( \\tilde X \\)"}</MathJax> every single period, forever. 
        Under this framework, the market value of any asset or claim equals its expected periodic cash flow divided by its required expected return (<MathJax inline>{"\\( \\text{Value} = \\frac{E[\\text{Cash Flow}]}{\\text{Rate}} \\)"}</MathJax>).
        </p>

        <p className='heading2'>Cash Flow Decomposition</p>
        <p>
        Before deciding on a single discount rate for the entire firm, we must consider the effect of leverage on a firm's components. 
        If a firm has both debt and equity, a portion of the future cash flows generated by the firm's assets will be used to pay debtholders, and the remaining portion will belong to equity holders. 
        </p>
        <p>
        Let <MathJax inline>{"\\( D \\)"}</MathJax> be the constant market value of the firm's outstanding debt, and let <MathJax inline>{"\\( r_d \\)"}</MathJax> be the expected return required by debtholders. 
        Every period, the firm pays a constant contractual interest amount to its debtholders. 
        Assuming no corporate taxes for now, we can decompose the firm's total cash flow <MathJax inline>{"\\( \\tilde X \\)"}</MathJax> each period into the portion paid to debtholders (<MathJax inline>{"\\( \\tilde X_d \\)"}</MathJax>) and the residual portion that flows to equity holders (<MathJax inline>{"\\( \\tilde X_e \\)"}</MathJax>):
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            \\tilde X = \\overbrace{\\tilde X_d}^{\\text{Debt Share}} + \\overbrace{(\\tilde X - \\tilde X_d)}^{\\text{Equity Share, } \\tilde X_e}
            \\end{aligned}
          \\]`}
        </MathJax>  
        If we take the expectation, we get 
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            E[\\tilde X] = E[\\tilde X_d] + E[\\tilde X_e]
            \\end{aligned}
          \\]`}
        </MathJax> 
        </p>

        <p>
        We can write the total current market value of the firm (<MathJax inline>{"\\( V \\)"}</MathJax>) as the sum of its debt market value (<MathJax inline>{"\\( D \\)"}</MathJax>) and equity market value (<MathJax inline>{"\\( E \\)"}</MathJax>):
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
              V = D + E
            \\end{aligned}
          \\]`}
        </MathJax>  
        Using our perpetuity valuation rules, we have
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
              V = \\frac{E[\\tilde X]}{r} \\quad \\quad D = \\frac{E[\\tilde X_d]}{r_d}  \\quad \\quad E = \\frac{E[\\tilde X_e]}{r_e}
            \\end{aligned}
          \\]`}
        </MathJax>  
        where <MathJax inline>{"\\( r_d \\)"}</MathJax> is the firm's cost of debt and <MathJax inline>{"\\( r_e \\)"}</MathJax> is the firm's cost of equity.
        Substituting these definitions back into our expected cash flow equation yields:
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
              &Vr = Dr_d +  Er_e \\\\
              &\\Leftrightarrow r = \\frac{D}{V}r_d + \\frac{E}{V}r_e
            \\end{aligned}
          \\]`}
        </MathJax>  
        The expected return required on the firm's assets is therefore a weighted average of the expected returns required by debt and equity investors, with the weights determined by the market values of each claim. 
        Thus, we call <MathJax inline>{"\\( r \\)"}</MathJax> the weighted average cost of capital (WACC). 
        </p>

        <p>
        The difference between the debt and equity costs of capital comes directly from the payoff priority. 
        Debtholders receive their promised payment first, making their cash flow streams relatively stable across different states of the world. 
        Equity holders receive only the residual cash flows after debtholders have been fully satisfied, meaning their payoff fluctuates significantly with the firm's operational performance. 
        Because equity investors bear this amplified volatility and systematic risk, they require a higher expected return (<MathJax inline>{"\\( r_e > r_d \\)"}</MathJax>) than debtholders.
        </p>

        <p className='heading2'>Estimating the WACC</p>
        <p>
        To solve for the WACC, we estimate <MathJax inline>{"\\( D \\)"}</MathJax> and <MathJax inline>{"\\( E \\)"}</MathJax> using the market values of the company's debt and equity respectively. 
        </p>
        <p>
        We estimate the equity cost of capital, <MathJax inline>{"\\( r_e \\)"}</MathJax>, using the CAPM equation 
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
              r_e = r_f + \\beta(r_m - r_f)
            \\end{aligned}
          \\]`}
        </MathJax>  
        This requires us to estimate the firm's beta, the risk-free rate (<MathJax inline>{"\\( r_f \\)"}</MathJax>) and the market risk premium (<MathJax inline>{"\\( r_m - r_f \\)"}</MathJax>) as well.
        </p>
        <p>
        We often estimate the debt cost of capital, <MathJax inline>{"\\( r_d \\)"}</MathJax>, using the yield to maturity on the firm's traded bonds.
        </p>
        <p className='heading2'>Market Estimates</p>
        <p>
        Notice that the equation for the WACC includes the total firm value (<MathJax inline>{"\\( V \\)"}</MathJax>). This is the quantity that we were trying to 
          estimate in the first place! Additionally, we are relying on market estimates for the equity and debt cost of capital, when the whole point of a DCF analysis may be to
          argue that the market has incorrectly priced the company. What gives?
        </p>
        <p>
        A DCF analysis combines two different sources of information: market prices are used to estimate the discount rate (the market's assessment of risk), 
          while an analyst supplies forecasts of future cash flows.
        </p>
        <p>
        For a publicly traded company, with liquid debt and equity, we may still use WACC in a DCF analysis if we believe that the 
        expected future cash flows of the company are different than what is reflected in market prices. 
        We may believe that market prices accurately reflect the risk of the firm's operating assets and capital structure, 
        while at the same time believing that the market has underestimated or overestimated the firm's future operating cash flows. 
        In that case, market prices provide reasonable estimates of the discount rate, but our own cash-flow forecasts may imply a different intrinsic value.
        </p>
        <p>
        In the case of a private company, we cannot infer the required returns directly from the firm's own market prices. 
        Instead, we estimate the firm's cost of capital by examining comparable publicly traded firms. 
        We "unlever" the comparables' betas to estimate the risk of their underlying assets, then "relever" those betas to reflect the target firm's expected capital structure before applying the CAPM
        (see Example 1).
        </p>
        <p className='heading2'>Taxes</p>
        <p>
        To bring the WACC closer to reality, we introduce a corporate tax rate <MathJax inline>{"\\( \\tau_c \\)"}</MathJax>. 
        When a firm pays corporate taxes, the government becomes a third claimant on the firm's operating cash flows. 
        However, because governments typically treat debt interest as a tax-deductible expense, a levered firm generates an interest tax shield that increases the total cash flow available to its investors.
        </p>
        
        <p>
        Every period, the firm pays its constant interest expense <MathJax inline>{"\\( r_d D \\)"}</MathJax> out of pre-tax cash flows. 
        This makes the firm's taxable income <MathJax inline>{"\\( \\tilde X - r_d D \\)"}</MathJax>, leading to a tax bill of <MathJax inline>{"\\( \\tau_c(\\tilde X - r_d D) \\)"}</MathJax>. 
        If we subtract this tax payment from the original pre-tax operating cash flow, we find the total after-tax cash flow available to be split between debt and equity investors (<MathJax inline>{"\\( \\tilde X_{\\text{investors}} \\)"}</MathJax>):
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            \\tilde X_{\\text{investors}} &= \\tilde X - \\tau_c(\\tilde X - r_d D) \\\\
            &= \\tilde X(1 - \\tau_c) + \\tau_c r_d D
            \\end{aligned}
          \\]`}
        </MathJax>
        Taking the expectation gives us
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            E[\\tilde X_{\\text{investors}}] = E[\\tilde X](1 - \\tau_c) + \\tau_c r_d D
            \\end{aligned}
          \\]`}
        </MathJax>
        The first term, <MathJax inline>{"\\( E[\\tilde X](1 - \\tau_c) \\)"}</MathJax>, represents the firm's completely unlevered after-tax cash flow. 
        The second term, <MathJax inline>{"\\( \\tau_c r_d D \\)"}</MathJax>, is the perpetual cash flow stream generated by the interest tax shield.
        </p>

        <p className='heading2'>The After-Tax WACC</p>
        <p>
        Just like in our tax-free model, the total cash flow distributed to investors must equal the sum of the cash flows paid to debtholders and equity holders (<MathJax inline>{"\\( E[\\tilde X_{\\text{investors}}] = E[\\tilde X_d] + E[\\tilde X_e] \\)"}</MathJax>). 
        Substituting our pricing definitions <MathJax inline>{"\\( D = \\frac{E[\\tilde X_d]}{r_d} \\)"}</MathJax> and <MathJax inline>{"\\( E = \\frac{E[\\tilde X_e]}{r_e} \\)"}</MathJax> gives:
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            &E[\\tilde X](1 - \\tau_c) + \\tau_c r_d D = r_d D + r_e E \\\\
            &\\Leftrightarrow E[\\tilde X](1 - \\tau_c) = r_d D(1 - \\tau_c) + r_e E
            \\end{aligned}
          \\]`}
        </MathJax>
        By definition, the after-tax weighted average cost of capital (<MathJax inline>{"\\( r \\)"}</MathJax>) is the single discount rate that values the firm's unlevered after-tax cash flows against the total levered 
          firm value (<MathJax inline>{"\\( V_L = D + E \\)"}</MathJax>), such that <MathJax inline>{"\\( V_L = \\frac{E[\\tilde X](1 - T_c)}{r} \\)"}</MathJax>. 
        Substituting this into our equation yields:
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            &V_Lr = r_d D(1 - \\tau_c) + r_e E \\\\
            &\\Leftrightarrow r = \\frac{D}{V_L}r_d(1 - \\tau_c) + \\frac{E}{V_L}r_e
            \\end{aligned}
          \\]`}
        </MathJax>
        Because corporate tax law subsidizes a fraction <MathJax inline>{"\\( \\tau_c \\)"}</MathJax> of the interest expense, the net economic cost of debt to the firm is effectively pulled down to <MathJax inline>{"\\( r_d(1 - T_c) \\)"}</MathJax>, which ultimately drives down the firm's overall cost of capital.
        </p>
        <p>
        Thus, the standard WACC formula is
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            \\text{WACC} = \\frac{D}{D+E}r_d(1 - \\tau_c) + \\frac{E}{D+E}r_e
            \\end{aligned}
          \\]`}
        </MathJax>
        </p>
        <p className='heading2'>Numerical Example</p>
        <p>
        Now that we've seen how to calculate the WACC, let's return to the setup we considered at the beginning. Suppose an analyst predicts 
        a firm's cash-flows over the next 5 years as follows (with a terminal value at the end of the forecast period):
        </p>
        <table class="minimal-table" style={{ maxWidth: "300px"}}>
        <thead>
          <tr>
            <th>Year</th>
            <th>Cash Flow (in millions of $)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td>400</td>
          </tr>
          <tr>
            <td>2</td>
            <td>500</td>
          </tr>
          <tr>
            <td>3</td>
            <td>550</td>
          </tr>
          <tr>
            <td>4</td>
            <td>600</td>
          </tr>
          <tr>
            <td>5</td>
            <td>800</td>
          </tr>
          <tr>
            <td>Terminal Value</td>
            <td>12,500</td>
          </tr>
        </tbody>
      </table>
      <p>
      Suppose the firm has outstanding bonds with a YTM of 6% and a CAPM beta of 1.2. The market value of the firm's equity is $8 billion, and the market value of the firm's debt is $2 billion, giving it a current market enterprise value of $10 billion. Suppose we estimate the risk-free rate and equity premium using historical data to be 4% and 5% respectively. 
      Assume a corporate tax rate of 21%.
      </p>
      <p>
      To calculate the firm's WACC, we first need to find its cost of debt and cost of equity. 
      </p>
      <p>
      We estimate the firm's cost of debt using the YTM on its bonds.
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            r_d = 0.06
            \\end{aligned}
          \\]`}
        </MathJax>
      </p>
      <p>
      We estimate the firm's cost of equity using CAPM.
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            r_e &= r_f + \\beta(r_m - r_f) \\\\
            &= 0.04 + 1.2(0.05) \\\\
            &= 0.1
            \\end{aligned}
          \\]`}
        </MathJax>
      </p>
      <p>
      Next, we use the WACC formula. To estimate the WACC, we use the market values of the firm's debt and equity to estimate the firm's current capital structure.
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            \\text{WACC} &= \\frac{D}{D+E}r_d(1 - \\tau_c) + \\frac{E}{D+E}r_e \\\\
            &= \\frac{2}{10}(0.06)(1 - 0.21) + \\frac{8}{10}(0.1) \\\\
            &= 8.948\\%
            \\end{aligned}
          \\]`}
        </MathJax>
      </p>
      <p>
      Finally, we discount the forecasted free cash flows at the firm's WACC to estimate its intrinsic enterprise value.
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            V &= \\frac{400}{1.08948} + \\frac{500}{(1.08948)^2} + \\frac{550}{(1.08948)^3} + \\frac{600}{(1.08948)^4} + \\frac{800 + 12,500}{(1.08948)^5} \\\\
            &\\approx \\$10.304 \\text{ billion}
            \\end{aligned}
          \\]`}
        </MathJax>  
      </p>
      <p>
      This DCF valuation is approximately $10.3 billion, compared to the market enterprise value of $10 billion. This suggests that, given our assumptions about future cash flows and the firm's cost of capital, 
      the company appears to be slightly undervalued by the market.
      </p>
      <p className='heading2'>Examples</p>
          <ExampleBox solution={
            <>
              <p>
              To find a beta using comparable firms, we first unlever each comparable firm's beta, or remove the effect of debt from the beta estimate. We do this using the following relationship: 
              <MathJax className='math-container'>
                {`\\[
                  \\begin{aligned}
                  \\beta_U = \\frac{\\beta_L}{1 + (1-\\tau_c)\\frac{D}{E}}
                  \\end{aligned}
                \\]`}
              </MathJax>  
              where <MathJax inline>{"\\( \\beta_L \\)"}</MathJax> is the companies levered beta, or the beta that comes out of estimating CAPM with historical stock prices, and <MathJax inline>{"\\( \\beta_U \\)"}</MathJax> is the company's unlevered beta. 
              Using this formula, we get 
              </p>
            <table class="minimal-table" style={{ maxWidth: "300px"}}>
            <thead>
              <tr>
                <th>Company</th>
                <th><MathJax inline>{"\\( \\beta_L \\)"}</MathJax></th>
                <th>Debt (in millions of $)</th>
                <th>Equity (in millions of $)</th>
                <th><MathJax inline>{"\\( \\beta_U \\)"}</MathJax></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>A</td>
                <td>1</td>
                <td>50</td>
                <td>1,000</td>
                <td><MathJax inline>{"\\( \\frac{1}{1 + (1 - 0.21)\\frac{50}{1000}} = 0.962\\)"}</MathJax></td>
              </tr>
              <tr>
                <td>B</td>
                <td>1.8</td>    
                <td>600</td>
                <td>600</td>
                <td><MathJax inline>{"\\( \\frac{1.8}{1 + (1 - 0.21)\\frac{600}{600}} = 1.006 \\)"}</MathJax></td>
              </tr>
              <tr>
                <td>C</td>
                <td>1.2</td>
                <td>150</td>
                <td>900</td>
                <td><MathJax inline>{"\\( \\frac{1.2}{1 + (1 - 0.21)\\frac{150}{900}} = 1.06 \\)"}</MathJax></td>
              </tr>
              <tr>
                <td>D</td>
                <td>1.3</td>
                <td>400</td>
                <td>800</td>
                <td><MathJax inline>{"\\( \\frac{1.3}{1 + (1 - 0.21)\\frac{400}{800}} = 0.932\\)"}</MathJax></td>
              </tr>
              <tr>
                <td>E</td>
                <td>1.5</td>
                <td>800</td>
                <td>1000</td>
                <td><MathJax inline>{"\\( \\frac{1.5}{1 + (1 - 0.21)\\frac{800}{1000}} = 0.919\\)"}</MathJax></td>
              </tr>
            </tbody>
          </table>
          <p>Notice that firms with unusually high leverage (such as Company B) have their betas reduced substantially when we remove the effect of leverage, while firms with little debt (such as Company A) change very little.</p>
          <p>
          Next, we take the average of the unlevered betas and then relever the result to match the capital structure of the firm we are trying to value. 
          The average of the unlevered betas is 
          <MathJax className='math-container'>
            {`\\[
              \\begin{aligned}
              \\frac{0.962 + 1.006 + 1.06 + 0.932 + 0.919}{5} = 0.976
              \\end{aligned}
            \\]`}
          </MathJax> 
          To relever this average, we rearrange the formula to solve for <MathJax inline>{"\\( \\beta_L \\)"}</MathJax> in terms of <MathJax inline>{"\\( \\beta_U \\)"}</MathJax>.
          <MathJax className='math-container'>
            {`\\[
              \\begin{aligned}
              \\beta_L = \\beta_U\\left(1 + (1-\\tau_c)\\frac{D}{E}\\right)
              \\end{aligned}
            \\]`}
          </MathJax>  
          </p>
          <p>
          Recall that the firm has $2 million in debt and $8 million in equity. Thus, the relevered beta is given by 
          <MathJax className='math-container'>
            {`\\[
              \\begin{aligned}
              \\beta_L = 0.976\\left(1 + (1-0.21)\\frac{2}{8}\\right) = 1.169
              \\end{aligned}
            \\]`}
          </MathJax>  
          </p>
            </>
          }>
            <div>
            <p><strong>Example 1:</strong> Assume all the same values from the example above, but suppose the company is private, so we can't estimate the firm's beta directly (since there is no available stock price data to use). We've identified 5 comparable companies 
            and calculated CAPM betas for each company. We've also collected the market value of each firm's debt and equity:
            </p>
            <table class="minimal-table" style={{ maxWidth: "300px"}}>
            <thead>
              <tr>
                <th>Company</th>
                <th>Beta</th>
                <th>Debt (in millions of $)</th>
                <th>Equity (in millions of $)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>A</td>
                <td>1</td>
                <td>50</td>
                <td>1,000</td>
              </tr>
              <tr>
                <td>B</td>
                <td>1.8</td>    
                <td>600</td>
                <td>600</td>
              </tr>
              <tr>
                <td>C</td>
                <td>1.2</td>
                <td>150</td>
                <td>900</td>
              </tr>
              <tr>
                <td>D</td>
                <td>1.3</td>
                <td>400</td>
                <td>800</td>
              </tr>
              <tr>
                <td>E</td>
                <td>1.5</td>
                <td>800</td>
                <td>1000</td>
              </tr>
            </tbody>
          </table>
          <p>
          If we weight each comparable firm equally, what beta should we use to find the firm's cost of equity?
          </p>
          </div>
          </ExampleBox>
      </MathJaxContext>
      <PageNavigator group="CS"/>
      </div>
  );
}

export default CS2;